import { ActionModal } from "./ActionModal";
import {
  EXECUTE_CONFIRM_DISCLOSURE,
  EXECUTE_CONFIRM_TITLE,
} from "../../vocabulary";
import type { PlanStepView } from "../../types";

/**
 * The one confirmation in this dashboard that guards a write to somebody
 * else's account (backend issue 50).
 *
 * Every other modal here guards a record we keep about a hotel — a lock, a
 * note, a condition, a fix report — all of them reversible by editing a row.
 * This one leaves the building: it publishes a container or mutates a Google
 * Ads setting, immediately, under the operator's name.
 *
 * So it shows two things and not a third. It shows **the step**, in the
 * catalog's words, because that is what the operator was reading when they
 * reached for the button. And it shows **what the executor will do**, from the
 * server, because the step's prose describes a repair in general and the
 * executor does one specific thing — and where those differ, the operator is
 * consenting to the second one.
 *
 * What it deliberately does NOT do is ask for a note. The machine writes its
 * own account of what it changed, and a box inviting a person to describe a
 * change they did not make is how a plausible sentence enters the record.
 */
export function ExecuteStepModal({
  step,
  onSubmit,
  onClose,
  pending,
}: {
  step: PlanStepView;
  onSubmit: () => void;
  onClose: () => void;
  pending?: boolean;
}) {
  return (
    <ActionModal
      title={EXECUTE_CONFIRM_TITLE}
      submitLabel="Apply the change"
      onSubmit={onSubmit}
      onClose={onClose}
      pending={pending}
    >
      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">The step</div>
        <p className="text-sm text-content-primary">
          {step.description ?? step.catalogRef}
        </p>
      </div>
      {step.canExecute.summary && (
        <div className="rounded-md bg-[#E7F1F3] px-3 py-2.5">
          <div className="text-xs font-medium text-brand-primary mb-1">
            What this will do
          </div>
          <p className="text-sm text-content-primary">
            {step.canExecute.summary}
          </p>
        </div>
      )}
      <p className="text-xs text-content-secondary">
        {EXECUTE_CONFIRM_DISCLOSURE}
      </p>
    </ActionModal>
  );
}
